import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

export default function Departments() {
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const load = () => api.get("/departments").then((r) => setDepartments(r.data)).catch((e) => toast.error(e.friendlyMessage));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/departments/${editing.id}`, { name, description });
        toast.success("Department updated");
      } else {
        await api.post("/departments", { name, description });
        toast.success("Department created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e.friendlyMessage); }
  };

  const remove = async (d) => {
    try {
      await api.delete(`/departments/${d.id}`);
      toast.success("Department deleted");
      load();
    } catch (e) { toast.error(e.friendlyMessage); }
  };

  return (
    <div className="animate-fade-in space-y-5" data-testid="departments-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Administration</div>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900">Departments</h1>
        </div>
        <Button data-testid="department-add-button" onClick={() => { setEditing(null); setName(""); setDescription(""); setOpen(true); }}
          className="bg-primary font-semibold hover:bg-[#1E3A8A]">
          <Plus className="mr-1.5 h-4 w-4" /> Add Department
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {departments.map((d) => (
          <div key={d.id} data-testid={`department-card-${d.id}`}
            className="group rounded-xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-start justify-between">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-50">
                <Building2 className="h-5 w-5 text-teal-700" />
              </span>
              <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                <Button size="sm" variant="ghost" data-testid={`department-edit-${d.id}`}
                  onClick={() => { setEditing(d); setName(d.name); setDescription(d.description || ""); setOpen(true); }}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" data-testid={`department-delete-${d.id}`}
                  className="text-rose-600 hover:text-rose-700" onClick={() => remove(d)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div className="mt-3 font-heading text-base font-bold text-slate-900">{d.name}</div>
            <div className="mt-1 text-xs text-slate-500">{d.description || "No description"}</div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="department-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Department" : "Add Department"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="department-form-name" placeholder="Department name" value={name} onChange={(e) => setName(e.target.value)} />
            <Input data-testid="department-form-description" placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="department-form-save" onClick={save} disabled={!name.trim()} className="bg-primary hover:bg-[#1E3A8A]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
